import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, TargetIcon, EyeIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { CTASection } from '../components/CTASection';
import { DemoModal } from '../components/DemoModal';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/PageHero';
import { coreValues, leadershipHighlights } from '../data/company';
export function WhoWeArePage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  return <motion.main initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.4
  }} className="min-h-screen w-full bg-stark-white overflow-x-hidden" data-id="element-446" data-mp="3r07chld6ytct">
      <Header onOpenDemo={() => setIsDemoModalOpen(true)} data-id="element-447" data-mp="ijc79z8x8wvk" />

      <PageHero eyebrow="Who We Are" title="Built by operators. Sharpened by technology." description="ROI Complete is the evolution of decades of hands-on consulting experience combined with modern business intelligence." data-id="element-448" data-mp="ikx1fnrbqdsw" />

      <section className="py-24 md:py-32" data-id="element-449" data-mp="17dcedulzw2qc">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center" data-id="element-450" data-mp="17c16pjpfiaty">
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="space-y-6" data-id="element-451" data-mp="tkwf94w7soup">
            <span className="text-royal-blue text-xs font-bold uppercase tracking-[0.2em]" data-id="element-452" data-mp="anlgdlehvxfs">
              Our Story
            </span>
            <h2 className="text-midnight-navy text-3xl md:text-4xl font-montserrat font-extrabold leading-tight" data-id="element-453" data-mp="fnr1sju68npf">
              Decades spent building restaurants, fixing operations, and
              rebuilding profitability.
            </h2>
            <div className="space-y-5 text-midnight-navy/60 text-lg leading-relaxed" data-id="element-454" data-mp="27liseo5shzpo">
              <p data-id="element-455" data-mp="23jnrvlvonqcy">
                Our work started in kitchens and dining rooms — opening
                restaurants, standardizing operations, and turning
                underperforming locations into profitable ones. That experience
                shaped a simple belief: most businesses do not have a revenue
                problem, they have a visibility problem.
              </p>
              <p data-id="element-456" data-mp="2m9x29j1miofq">
                ROI Complete is the evolution of that consulting work. We
                combined the playbooks that consistently produced results with
                technology that puts the same intelligence in front of operators
                every single day.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-457" data-mp="3a0j8r5vzc0nm">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/9a6ff696-b87b-492d-a00d-c659f58b3532.jpg" alt="ROI Complete consultants reviewing financial performance with a restaurant management team" className="w-full h-auto rounded-2xl shadow-2xl shadow-midnight-navy/15" data-id="element-458" data-mp="3mi3j0kcsyzir" />
          </motion.div>
        </div>
      </section>

      <section className="bg-cool-gray py-24 md:py-32" data-id="element-459" data-mp="9378rh7xz491">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6" data-id="element-460" data-mp="94iehmky6m2s">
          {[{
          icon: TargetIcon,
          label: 'Mission',
          body: 'Empower businesses with the tools, strategies, and insights needed to maximize profitability and operational excellence.'
        }, {
          icon: EyeIcon,
          label: 'Vision',
          body: 'Become the leading operational intelligence platform for hospitality and service businesses.'
        }].map((item, idx) => <motion.div key={item.label} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: idx * 0.1
        }} className="bg-stark-white p-10 md:p-12 rounded-2xl border border-midnight-navy/10 space-y-6" data-id="element-461" data-mp="1tkoi4s2gw8i1" data-mpi={idx}>
              <div className="w-12 h-12 rounded-2xl bg-royal-blue/10 text-royal-blue flex items-center justify-center" data-id="element-462" data-mp="15t9dutr4rh45">
                <item.icon className="w-6 h-6" data-id="element-463" data-mp="2mandt6b633vo" />
              </div>
              <h3 className="text-midnight-navy text-2xl font-montserrat font-extrabold" data-id="element-464" data-mp="2h97q16hluj9l">
                {item.label}
              </h3>
              <p className="text-midnight-navy/60 text-lg leading-relaxed" data-id="element-465" data-mp="2tsd7ba1xitjh">
                {item.body}
              </p>
            </motion.div>)}
        </div>
      </section>

      <section className="py-24 md:py-32" data-id="element-466" data-mp="2fuwxdokfchmz">
        <div className="max-w-7xl mx-auto px-6" data-id="element-467" data-mp="2kqvj14edbljp">
          <div className="max-w-2xl mb-16 space-y-5" data-id="element-468" data-mp="2ktrhquy1ie97">
            <span className="text-royal-blue text-xs font-bold uppercase tracking-[0.2em]" data-id="element-469" data-mp="38iaoh61fyiz6">
              Core Values
            </span>
            <h2 className="text-midnight-navy text-3xl md:text-5xl font-montserrat font-extrabold leading-tight tracking-tight" data-id="element-470" data-mp="3dif4kuyftbov">
              The standards behind every engagement.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-471" data-mp="33o6hwnw38cym">
            {coreValues.map((value, idx) => <motion.div key={value.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: idx * 0.07
          }} className="p-6 border-t-2 border-royal-blue space-y-3" data-id="element-472" data-mp="1xvvr455dc0" data-mpi={idx}>
                <h3 className="text-midnight-navy text-lg font-montserrat font-bold" data-id="element-473" data-mp="3n59fizwdzbdd">
                  {value.title}
                </h3>
                <p className="text-midnight-navy/50 text-sm leading-relaxed" data-id="element-474" data-mp="3ddvfmr0iw9jp">
                  {value.body}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="bg-midnight-navy py-24 md:py-32" data-id="element-475" data-mp="34dq9slaz9j7n">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-16 items-center" data-id="element-476" data-mp="34f9q2bwmvu9f">
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="lg:col-span-2" data-id="element-477" data-mp="2zggmbb75fhmg">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/1bad56b9-0463-477b-9e71-70660665ba6f.jpg" alt="Aaron Gadson, Founder and CEO of ROI Complete" className="w-full h-auto rounded-2xl shadow-2xl" data-id="element-478" data-mp="1svb5b7i8y6aq" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-3 space-y-8" data-id="element-479" data-mp="15571p05266jq">
            <span className="text-royal-blue text-xs font-bold uppercase tracking-[0.2em]" data-id="element-480" data-mp="36z9c9bclsgcn">
              Leadership
            </span>
            <div className="space-y-2" data-id="element-481" data-mp="3fzp9w46o0t9l">
              <h2 className="text-stark-white text-4xl md:text-5xl font-montserrat font-extrabold tracking-tight" data-id="element-482" data-mp="3g0gxarq3zkos">
                Aaron Gadson
              </h2>
              <p className="text-royal-blue font-bold uppercase tracking-widest text-sm" data-id="element-483" data-mp="3pxlnr3qzm2be">
                Founder &amp; CEO
              </p>
            </div>
            <p className="text-stark-white/60 text-lg leading-relaxed" data-id="element-484" data-mp="3kyih5evczo1b">
              Aaron has spent his career on both sides of the business — running
              kitchens and leading multi-unit operations, then advising owners on
              how to build systems that hold. He founded ROI Complete to give
              operators the same executive-level insight that large groups have
              always had, delivered through technology.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3" data-id="element-485" data-mp="r6pc3oyvp3su">
              {leadershipHighlights.map(highlight => <li key={highlight} className="flex items-center gap-3 text-stark-white/80 font-medium" data-id="element-486" data-mp="w3ftcfmf65fu">
                  <span className="w-5 h-5 rounded-full bg-royal-blue/20 text-royal-blue flex items-center justify-center shrink-0" data-id="element-487" data-mp="m6b2w3ljjnt8">
                    <CheckIcon className="w-3 h-3" data-id="element-488" data-mp="8eed467chq00" />
                  </span>
                  {highlight}
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      <CTASection onOpenDemo={() => setIsDemoModalOpen(true)} data-id="element-489" data-mp="3unz4pxv22tew" />
      <Footer data-id="element-490" data-mp="3fjxfd4ochk4" />
      <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} data-id="element-491" data-mp="3h4qeybcbsfv" />
    </motion.main>;
}